import React, { useEffect, useState, Fragment } from 'react';
import { collection, query, where, getDocs, getDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../Auth/Auth';
import { Link, Navigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import BreadCrumb from '../BreadCrumb';
import moment from 'moment';
import { MoonLoader } from 'react-spinners';
import { UserGroupIcon, ClockIcon } from '@heroicons/react/24/outline';
import logger from '../../utils/logger';
import { Dialog, Transition } from '@headlessui/react';

const pages = [
  { name: 'Service Advisor', to: '/vehicles', component: Link, current: false },
  { name: 'Monitor Mechanic Tasks', to: '/vehicles/managertasks', component: Link, current: true },
];

const ManagerMechanicTasks = () => {
  const [mechanics, setMechanics] = useState([]);
  const [selectedMechanic, setSelectedMechanic] = useState('');
  const [verificationComments, setVerificationComments] = useState('');
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { user, role, loading } = useAuth();

  useEffect(() => {
    const fetchMechanics = async () => {
      const q = query(collection(db, "employees"), where("role", "==", "Mechanic"));
      const querySnapshot = await getDocs(q);
      setMechanics(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchMechanics();
  }, []);

  useEffect(() => {
    if (selectedMechanic) {
      const fetchTasks = async () => {
        const q = query(collection(db, "tasks"), where("assignedTo", "==", selectedMechanic));
        const querySnapshot = await getDocs(q);
        const loadedTasks = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          allSubtasksCompleted: doc.data().subtasks.every(subtask => subtask.status === 'completed')
        }));
        setTasks(loadedTasks);
      };
      fetchTasks();
    }
  }, [selectedMechanic]);

  const handleMechanicChange = (e) => {
    setSelectedMechanic(e.target.value);
    setSelectedTask(null);
  };

  const handleTaskSelection = (task) => {
    setSelectedTask(task);
    setShowModal(true);
    setShowVerificationModal(task.allSubtasksCompleted && task.status === 'completed' && !task.taskVerified);
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    const taskRef = doc(db, "tasks", taskId);
    try {
      await updateDoc(taskRef, {
        status: newStatus,
        updatedAt: Timestamp.now(),
        ...(newStatus === 'completed' && { completedAt: Timestamp.now() })
      });
      toast.success('Task status updated');
      setTasks(tasks.map(task => task.id === taskId ? { ...task, status: newStatus, ...(newStatus === 'completed' && { completedAt: Timestamp.now() }) } : task));
    } catch (error) {
      logger.error('Error updating task status:', error);
      toast.error('Failed to update task status');
    }
  };

  const updateSubtaskStatus = async (taskId, subtaskId, newStatus) => {
    const taskRef = doc(db, "tasks", taskId);
    const taskSnapshot = await getDoc(taskRef);
    if (taskSnapshot.exists()) {
      const taskData = taskSnapshot.data();
      const updatedSubtasks = taskData.subtasks.map(subtask =>
        subtask.id === subtaskId ? { ...subtask, status: newStatus } : subtask
      );

      const allSubtasksCompleted = updatedSubtasks.every(subtask => subtask.status === 'completed');

      await updateDoc(taskRef, {
        subtasks: updatedSubtasks,
        updatedAt: Timestamp.now(),
        ...(allSubtasksCompleted && { status: 'completed', completedAt: Timestamp.now() })
      });

      toast.success('Subtask status updated successfully');
      setTasks(tasks.map(task => task.id === taskId ? { ...task, subtasks: updatedSubtasks } : task));
      setSelectedTask(current => current && current.id === taskId ? { ...current, subtasks: updatedSubtasks } : current);

      if (allSubtasksCompleted) {
        const jobRef = doc(db, 'jobs', taskData.jobCardNo);
        const jobSnapshot = await getDoc(jobRef);

        if (jobSnapshot.exists()) {
          const jobData = jobSnapshot.data();
          const updatedTracker = [
            ...jobData.tracker,
            {
              Date: Timestamp.now(),
              State: 'Repair Concluded',
              comments: `All subtasks for task "${taskData.taskTitle}" have been completed.`
            }
          ];

          await updateDoc(jobRef, { tracker: updatedTracker });
        }
      }
    }
  };

  const verifyTask = async () => {
    if (selectedTask) {
      const taskRef = doc(db, "tasks", selectedTask.id);
      try {
        await updateDoc(taskRef, {
          taskVerified: true,
          verifiedBy: user.email,
          verifiedAt: Timestamp.now(),
          verificationComments
        });
        toast.success('Task verified successfully');
        setTasks(tasks.map(task => task.id === selectedTask.id ? { ...task, taskVerified: true, verificationComments } : task));
        setShowVerificationModal(false);
      } catch (error) {
        logger.error('Error verifying task:', error);
        toast.error('Failed to verify task');
      }
    }
  };

  const calculateTimeDifference = (start, end) => {
    if (!start || !end) return '';
    const duration = moment.duration(moment(end.toDate()).diff(moment(start.toDate())));
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    return `${hours}h ${minutes}m`;
  };

  const checkOnTime = (deadline, completedAt) => {
    if (!deadline || !completedAt) return null;
    const deadlineTime = moment(deadline.toDate());
    const completedTime = moment(completedAt.toDate());
    const difference = completedTime.diff(deadlineTime, 'hours', true);
    const isOnTime = difference <= 0;
    return {
      isOnTime,
      text: `${Math.abs(difference).toFixed(1)}h ${isOnTime ? 'early' : 'late'}`
    };
  };

  const getStatusBadge = (status) => {
    const badges = {
      'new': 'bg-blue-100 text-blue-800',
      'in-progress': 'bg-amber-100 text-amber-800',
      'completed': 'bg-green-100 text-green-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <MoonLoader size={50} color="#3B82F6" />
      </div>
    );
  }

  if (role !== 'Service Advisor' && role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <>
      <BreadCrumb pages={pages} />
      <div className="min-h-screen bg-gradient-to-br from-blue-500 via-cyan-500 to-cyan-600 py-4 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-4 text-center">
            <div className="flex items-center justify-center mb-2">
              <UserGroupIcon className="h-10 w-10 text-white mr-3" />
              <h1 className="text-3xl font-black text-white">Monitor Mechanic Tasks</h1>
            </div>
            <p className="text-blue-50 text-sm">Track and manage mechanic task progress</p>
          </div>

          {/* Mechanic Selection */}
          <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
            <label className="block text-xs font-bold text-gray-700 mb-2">Select Mechanic</label>
            <select
              value={selectedMechanic}
              onChange={handleMechanicChange}
              className="w-full px-4 py-2 rounded-xl border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm"
            >
              <option value="">Choose a mechanic</option>
              {mechanics.map(mechanic => (
                <option key={mechanic.id} value={mechanic.id}>
                  {mechanic.firstName} {mechanic.lastName}
                </option>
              ))}
            </select>
          </div>

          {/* Task Cards */}
          {tasks.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tasks.map(task => {
                const onTimeStatus = task.status === 'completed' ? checkOnTime(task.deadline, task.completedAt) : null;
                return (
                  <div key={task.id} className="bg-white rounded-xl shadow-lg p-4 hover:shadow-xl transition-shadow">
                    {/* Task Header */}
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-base font-bold text-gray-800 flex-1">{task.taskTitle}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(task.status)}`}>
                        {task.status}
                      </span>
                    </div>

                    {/* Task Details */}
                    <div className="space-y-1 text-xs mb-3">
                      <p className="text-gray-600">
                        <span className="font-semibold">Job:</span> {task.jobCardNo}
                      </p>
                      <p className="text-gray-600">
                        <span className="font-semibold">Assigned to:</span> {task.assignedToName}
                      </p>
                      <p className="text-gray-600">
                        <span className="font-semibold">Start:</span> {moment(task.startDate.toDate()).format('MMM D, h:mm A')}
                      </p>
                      <p className="text-gray-600">
                        <span className="font-semibold">Deadline:</span> {moment(task.deadline.toDate()).format('MMM D, h:mm A')}
                      </p>

                      {task.status === 'completed' && (
                        <>
                          <p className="text-gray-600">
                            <span className="font-semibold">Completed:</span> {moment(task.completedAt.toDate()).format('MMM D, h:mm A')}
                          </p>
                          {task.startedAt && (
                            <p className="text-gray-600">
                              <span className="font-semibold">Time to Start:</span> {calculateTimeDifference(task.startDate, task.startedAt)}
                            </p>
                          )}
                          <p className="text-gray-600">
                            <span className="font-semibold">Duration:</span> {calculateTimeDifference(task.deadline, task.completedAt)}
                          </p>
                          {onTimeStatus && (
                            <p className={`font-semibold ${onTimeStatus.isOnTime ? 'text-green-600' : 'text-red-600'}`}>
                              {onTimeStatus.text}
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    {/* View Button */}
                    <button
                      onClick={() => handleTaskSelection(task)}
                      className="w-full py-2 px-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm font-bold rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all"
                    >
                      View Details
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {selectedMechanic && tasks.length === 0 && (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <ClockIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No tasks assigned to this mechanic yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Task Details Modal */}
      <Transition appear show={showModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-xl bg-white p-6 shadow-xl transition-all">
                  <Dialog.Title className="text-xl font-bold text-gray-900 mb-4">
                    {selectedTask?.taskTitle}
                  </Dialog.Title>

                  {selectedTask && (
                    <>
                      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <p><span className="font-semibold">Status:</span> {selectedTask.status}</p>
                          <p><span className="font-semibold">Assigned By:</span> {selectedTask.assignedBy}</p>
                          <p><span className="font-semibold">Job Card:</span> {selectedTask.jobCardNo}</p>
                          <p><span className="font-semibold">Assigned To:</span> {selectedTask.assignedToName}</p>
                        </div>
                      </div>

                      {selectedTask.status !== 'completed' && (
                        <button
                          onClick={() => updateTaskStatus(selectedTask.id, 'completed')}
                          className="mb-4 px-4 py-2 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-colors"
                        >
                          Mark as Completed
                        </button>
                      )}

                      {/* Subtasks Table */}
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-2 py-1.5 text-left text-xs font-bold text-gray-700 uppercase">
                                Description
                              </th>
                              <th className="px-2 py-1.5 text-left text-xs font-bold text-gray-700 uppercase">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {selectedTask.subtasks.map((subtask) => (
                              <tr key={subtask.id}>
                                <td className="px-2 py-1.5 text-xs text-gray-900">{subtask.description}</td>
                                <td className="px-2 py-1.5">
                                  <select
                                    value={subtask.status}
                                    onChange={(e) => updateSubtaskStatus(selectedTask.id, subtask.id, e.target.value)}
                                    className="px-2 py-1 rounded-lg bg-white border-2 border-gray-300 text-xs focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                  >
                                    <option value="pending">Pending</option>
                                    <option value="in-progress">In Progress</option>
                                    <option value="completed">Completed</option>
                                  </select>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <button
                        onClick={() => setShowModal(false)}
                        className="mt-6 w-full px-4 py-2 bg-gray-600 text-white font-bold rounded-xl hover:bg-gray-700 transition-colors"
                      >
                        Close
                      </button>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Verification Modal */}
      <Transition appear show={showVerificationModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowVerificationModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white p-6 shadow-xl transition-all">
                  <Dialog.Title className="text-lg font-bold text-gray-900 mb-4">
                    Verify Task Completion
                  </Dialog.Title>
                  <textarea
                    value={verificationComments}
                    onChange={e => setVerificationComments(e.target.value)}
                    placeholder="Enter verification comments..."
                    className="w-full h-32 p-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                  />
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={verifyTask}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors"
                    >
                      Verify
                    </button>
                    <button
                      onClick={() => setShowVerificationModal(false)}
                      className="flex-1 px-4 py-2 bg-gray-600 text-white font-bold rounded-xl hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <ToastContainer />
    </>
  );
};

export default ManagerMechanicTasks;
